import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaPlus, FaTrash } from 'react-icons/fa';
import Select from 'react-select';
import offerService from '../services/offerService';
import currentAccountService from '../services/currentAccountService';
import productService from '../services/productService';
import api from '../services/api';

const OfferFormPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  
  // Form Ana Bilgileri
  const [formData, setFormData] = useState({
    current_account_id: '',
    offer_date: new Date().toISOString().split('T')[0],
    valid_until: '',
    currency: 'TRY',
    tax_rate: 20, // Varsayılan KDV %20
    notes: '',
    status: 'taslak'
  });

  // Teklif Kalemleri (Ürünler)
  const [items, setItems] = useState([
    { product_id: '', product_name: '', quantity: 1, unit: 'adet', unit_price: 0, total_price: 0 }
  ]);

  // Select verileri
  const [accounts, setAccounts] = useState([]);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [accData, prodData] = await Promise.all([
          currentAccountService.getAll(),
          productService.getAll()
        ]);
        setAccounts(accData);
        setProducts(prodData);

        if (id) {
          // Düzenleme modu: Mevcut teklifi çek
          const offer = await offerService.getById(id);
          setFormData({
            current_account_id: offer.current_account_id,
            offer_date: offer.offer_date ? new Date(offer.offer_date).toISOString().split('T')[0] : '',
            valid_until: offer.valid_until ? new Date(offer.valid_until).toISOString().split('T')[0] : '',
            currency: offer.currency,
            tax_rate: offer.tax_rate || 0,
            notes: offer.notes || '',
            status: offer.status
          });
          
          if (offer.items && offer.items.length > 0) {
            setItems(offer.items.map(item => ({
              ...item,
              quantity: Number(item.quantity),
              unit_price: Number(item.unit_price),
              unit: item.unit || 'adet',
              total_price: Number(item.total_price)
            })));
          }
        }
      } catch (error) {
        toast.error('Veriler yüklenirken hata oluştu.');
      }
    };
    loadData();
  }, [id]);

  // Genel Form Değişikliği
  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Kalem (Satır) Değişikliği
  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;

    // Ürün seçildiyse adını ve fiyatını otomatik doldur
    if (field === 'product_id') {
      const selectedProduct = products.find(p => p.id === parseInt(value));
      if (selectedProduct) {
        newItems[index].product_name = selectedProduct.name;
        newItems[index].unit_price = selectedProduct.selling_price;
        newItems[index].unit = selectedProduct.unit || 'adet';
        
        // Ürünün KDV oranını teklif geneline uygula
        if (selectedProduct.tax_rate !== undefined && selectedProduct.tax_rate !== null) {
          setFormData(prev => ({ ...prev, tax_rate: selectedProduct.tax_rate }));
        }
      }
    }

    // Toplam fiyatı hesapla
    if (field === 'quantity' || field === 'unit_price' || field === 'product_id') {
      newItems[index].total_price = newItems[index].quantity * newItems[index].unit_price;
    }

    setItems(newItems);
  };

  // Yeni Satır Ekle
  const addItem = () => {
    setItems([...items, { product_id: '', product_name: '', quantity: 1, unit: 'adet', unit_price: 0, total_price: 0 }]);
  };

  // Satır Sil
  const removeItem = (index) => {
    const newItems = items.filter((_, i) => i !== index);
    setItems(newItems);
  };

  // Hesaplamalar (KDV Dahil Mantığına Göre Güncellendi)
  const calculateGrandTotal = () => {
    return items.reduce((acc, item) => acc + (Number(item.total_price) || 0), 0);
  };

  const calculateSubTotal = () => {
    const rate = Number(formData.tax_rate) || 0;
    return calculateGrandTotal() / (1 + rate / 100);
  };

  const calculateTax = () => {
    return calculateGrandTotal() - calculateSubTotal();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.current_account_id) {
      toast.warning('Lütfen bir müşteri seçiniz.');
      return;
    }

    try {
      const dataToSend = {
        ...formData,
        valid_until: formData.valid_until || null,
        total_amount: calculateGrandTotal(),
        items: items.map(item => ({
          ...item,
          product_id: item.product_id || null // Boşsa null gönder
        }))
      };

      if (id) {
        await api.put(`/offers/${id}`, dataToSend);
        toast.success('Teklif güncellendi.');
      } else {
        await offerService.create(dataToSend);
        toast.success('Teklif başarıyla oluşturuldu.');
      }
      navigate('/offers');
    } catch (error) {
      toast.error(error.response?.data?.message || 'İşlem başarısız.');
    }
  };

  const accountOptions = accounts.map(acc => ({ value: acc.id, label: acc.company_name }));
  const productOptions = products.map(p => ({ value: p.id, label: p.name }));

  return (
    <div className="container-fluid mt-4">
      <div className="card shadow-sm">
        <div className="card-header bg-white">
          <h4 className="mb-0">{id ? 'Teklif Düzenle' : 'Yeni Teklif Oluştur'}</h4>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            {/* Üst Bilgiler */}
            <div className="row mb-4">
              <div className="col-md-6 mb-3">
                <label className="form-label">Müşteri</label>
                <Select
                  options={accountOptions}
                  value={accountOptions.find(opt => opt.value === formData.current_account_id)}
                  onChange={(option) => setFormData({ ...formData, current_account_id: option ? option.value : '' })}
                  placeholder="Seçiniz..."
                  isSearchable
                  isClearable
                  styles={{ menu: (provided) => ({ ...provided, zIndex: 9999 }) }}
                />
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label">Teklif Tarihi</label>
                <input type="date" name="offer_date" className="form-control" value={formData.offer_date} onChange={handleChange} required />
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label">Geçerlilik Tarihi</label>
                <input type="date" name="valid_until" className="form-control" value={formData.valid_until} onChange={handleChange} />
              </div>
              <div className="col-md-3 mb-3">
                <label className="form-label">KDV Oranı (%)</label>
                <select name="tax_rate" className="form-select" value={formData.tax_rate} onChange={handleChange}>
                  <option value="0">%0</option>
                  <option value="1">%1</option>
                  <option value="10">%10</option>
                  <option value="20">%20</option>
                </select>
              </div>
            </div>

            {/* Ürünler Tablosu */}
            <h5 className="mb-3">Ürünler / Hizmetler</h5>
            <div className="table-responsive mb-3">
              <table className="table table-bordered">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: '40%' }}>Ürün</th>
                    <th style={{ width: '15%' }}>Birim</th>
                    <th style={{ width: '10%' }}>Miktar</th>
                    <th style={{ width: '15%' }}>Birim Fiyat (KDV Dahil)</th>
                    <th style={{ width: '15%' }}>Toplam</th>
                    <th style={{ width: '5%' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, index) => (
                    <tr key={index}>
                      <td>
                        <Select
                          options={productOptions}
                          value={productOptions.find(opt => opt.value === item.product_id)}
                          onChange={(option) => handleItemChange(index, 'product_id', option ? option.value : '')}
                          placeholder="Seçiniz..."
                          isSearchable
                          isClearable
                          menuPortalTarget={document.body}
                          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                        />
                      </td>
                      <td>
                        <select className="form-select" value={item.unit} onChange={(e) => handleItemChange(index, 'unit', e.target.value)}>
                          <option value="adet">Adet</option>
                          <option value="kg">KG</option>
                          <option value="cuval">Çuval</option>
                        </select>
                      </td>
                      <td>
                        <input type="number" className="form-control" value={item.quantity} onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value))} min="1" required />
                      </td>
                      <td>
                        <input type="number" className="form-control" value={item.unit_price} onChange={(e) => handleItemChange(index, 'unit_price', parseFloat(e.target.value))} min="0" step="0.01" required />
                      </td>
                      <td className="text-end align-middle fw-bold">
                        {Number(item.total_price).toFixed(2)} TL
                      </td>
                      <td className="text-center align-middle">
                        {items.length > 1 && (
                          <button type="button" onClick={() => removeItem(index)} className="btn btn-sm btn-outline-danger"><FaTrash /></button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <button type="button" onClick={addItem} className="btn btn-sm btn-outline-primary mb-4"><FaPlus /> Satır Ekle</button>

            <div className="row justify-content-end">
              <div className="col-md-4">
                <div className="card bg-light">
                  <div className="card-body">
                    <div className="d-flex justify-content-between mb-1">
                      <span>Ara Toplam (KDV Hariç):</span>
                      <span className="fw-bold">{calculateSubTotal().toFixed(2)} TL</span>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span>KDV (%{formData.tax_rate}):</span>
                      <span className="fw-bold">{calculateTax().toFixed(2)} TL</span>
                    </div>
                    <div className="d-flex justify-content-between mb-2">
                      <span className="fw-bold fs-5">Genel Toplam:</span>
                      <span className="fw-bold fs-5">{calculateGrandTotal().toFixed(2)} TL</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-3 mt-4">
              <label className="form-label">Notlar</label>
              <textarea name="notes" className="form-control" rows="3" value={formData.notes} onChange={handleChange}></textarea>
            </div>

            <div className="d-flex justify-content-end mt-4">
              <button type="button" onClick={() => navigate('/offers')} className="btn btn-secondary me-2">İptal</button>
              <button type="submit" className="btn btn-primary">Teklifi Kaydet</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default OfferFormPage;
